<script setup>
import GameItem from "@shared/components/molecules/GameItem.vue"
import {useGameFilterProps} from "@shared/composables/useGameFilterProps"
import {useGameStore} from "@shared/stores/GameStore"
import { vElementVisibility } from '@vueuse/components'
import {useBreakpoints} from '@vueuse/core'
import {computed, onMounted, reactive, watch} from "vue"

const props = defineProps({
    ...useGameFilterProps(),
    group: {
        type: String,
        required: true
    },
    subCategory: {
        type: String,
        default: ''
    },
    subGroup: {
        type: String,
        default: ''
    },
    product: { // ~game provider
        type: String,
        default: ''
    },
    url: {
        type: [String, Object],
        required: false,
        default: () => ''
    },
    urlText: {
        type: String,
        required: false,
        default: 'SHOW MORE'
    },
    promotionsUrl: {
        type: [String, Object],
        required: false,
        default: ''
    },
    showMoreOnClick: {
        type: Boolean,
        default: () => false
    },
    showMoreLimit: {
        type: [Number, String],
        default: () => 0
    },
    showMoreCustomAmount: {
        type: Number,
        default: () => 0
    },
    showAll: {
        type: Boolean,
        default: () => false
    },
    displayAmounts: {
        type: Object,
        required: false
    },
    displayAmountsFixed: {
        type: Boolean,
        default: () => false
    },
    displayGameSpecials: {
        type: Boolean,
        default: () => false
    },
    breakpoints: {
        type: Object,
        default: () => ({sm: 300, md: 800, lg: 1300, xl: 1700})
    }
})

const gameStore = useGameStore()
const breakpoints = useBreakpoints(props.breakpoints)

const state = reactive({
    amountOfGames: 6,
    offset: 0,
    games: [],
    showMoreLimit: props.showMoreLimit
})

const calculateAmountOfGamesToDisplay = () => {
    if (props.showAll) {
        return 0
    }

    const defaultDisplayAmounts = { sm: 6, md: 9, lg: 10, xl: 10 }
    const { sm, md, lg, xl } = { ...defaultDisplayAmounts, ...props.displayAmounts }

    if (breakpoints.greaterOrEqual('xl').value) {
        return xl
    }

    if (breakpoints.greaterOrEqual('lg').value) {
        return lg
    }

    if (breakpoints.greaterOrEqual('md').value) {
        return md
    }

    return sm
}

const games = computed(() => gameStore.getGamesByCategory({
    category: props.group,
    subCategory: props.subCategory,
    subGroup: props.subGroup,
    product: props.product,
    limit: state.amountOfGames,
    offset: state.offset,
    filter: props.filter,
    sortBy: props.sortBy,
}))

const subGroupGames = computed(() => {
    const subGroupTitle = props.subGroup?.trim().toLowerCase()

    return gameStore.allGames
        .filter(game => !props.subGroup || (game.sub_groups && JSON.parse(game.sub_groups).some(subGroup => subGroup.title.trim().toLowerCase() === subGroupTitle)))
        .sort((a, b) => {
            const getOrder = (game) => JSON.parse(game.sub_groups).find(subGroup => subGroup.title.trim().toLowerCase() === subGroupTitle)?.sort || 0

            return getOrder(a) - getOrder(b)
        })
})

const gamesToShow = computed(() => props.subGroup ? subGroupGames.value : games.value.items)

const gamesCount = computed(() => props.subGroup ? subGroupGames.value.length : games.value.count.all)

const handleShowMore = () => {
    if (!props.displayAmountsFixed) {
        if (props.showMoreLimit) {
            if (state.showMoreLimit > 0) {
                state.showMoreLimit = state.showMoreLimit - 1
                state.amountOfGames += calculateAmountOfGamesToDisplay()
            }
        } else if (props.showMoreCustomAmount) {
            state.amountOfGames = props.showMoreCustomAmount
        } else {
            state.amountOfGames += calculateAmountOfGamesToDisplay()
        }
    }
}

watch(
    () => breakpoints.current().value,
    () => {
        state.amountOfGames = calculateAmountOfGamesToDisplay()
    }
)

watch(
    () => props.filter,
    () => {
        state.amountOfGames = calculateAmountOfGamesToDisplay()
    }
)

onMounted(() => {
    if (props.showMoreCustomAmount) {
        state.amountOfGames = calculateAmountOfGamesToDisplay()
        gamesToShow.value = props.showMoreCustomAmount
    } else {
        state.amountOfGames = calculateAmountOfGamesToDisplay()
    }
})
</script>

<template>
    <div
        :class="{
            gameslist: true,
            searchresults: filter,
            noresults: filter && gamesCount === 0
        }"
    >
        <p v-if="filter && gamesCount === 0" id="nosearchresultsmsg">
            Sorry, your search did not find any matching game, please try again.
        </p>
        <template v-else>
            <template v-if="displayGameSpecials">
                <div class="gameitem_special">
                    <GameItem
                        v-for="game in gamesToShow.slice(0,3)"
                        :key="game.id"
                        :game="game"
                    />
                </div>
                <GameItem
                    v-for="game in gamesToShow.slice(3)"
                    :key="game.id"
                    :game="game"
                />
            </template>

            <GameItem v-else
                v-for="game in gamesToShow"
                :key="game.id"
                :game="game"
            />
        </template>
    </div>

    <div class="gameslist__buttons d-flex justify-content-center m-0 pt-5">
        <template v-if="!showMoreLimit && !showMoreCustomAmount">
            <div v-if="url">
                <router-link :to="url">
                    <button>{{ urlText }}</button>
                </router-link>
            </div>
            <div v-else-if="!displayAmountsFixed && (gamesToShow.length && gamesCount > gamesToShow.length)">
                <div v-if="!showMoreOnClick" v-element-visibility="handleShowMore">
                    <button @click="handleShowMore">SHOW MORE</button>
                </div>
                <div v-else>
                    <button @click="handleShowMore">SHOW MORE</button>
                </div>
            </div>
        </template>

        <template v-else>
            <template v-if="url">
                <template v-if="promotionsUrl">
                    <template v-if="(showMoreCustomAmount > gamesToShow.length && gamesToShow.length < gamesCount) || (state.showMoreLimit > 0 && gamesToShow.length < gamesCount)">
                        <div v-if="!showMoreOnClick" v-element-visibility="handleShowMore">
                            <button @click="handleShowMore">SHOW MORE</button>
                        </div>
                        <div v-else>
                            <button @click="handleShowMore">SHOW MORE</button>
                        </div>
                    </template>
                    <template v-else>
                        <router-link :to="url">
                            <button class="gameslist__button--green">{{ urlText }}</button>
                        </router-link>
                        <router-link :to="promotionsUrl">
                            <button>PROMOTIONS</button>
                        </router-link>
                    </template>
                </template>
                <template v-else>
                    <router-link :to="url">
                        <button>{{ urlText }}</button>
                    </router-link>
                </template>
            </template>
        </template>
    </div>
</template>

<style lang="scss" scoped>
.gameslist {
    // grid-template-columns: repeat(1, 1fr);

    @media (min-width: 300px) {
        // grid-template-columns: repeat(2, 1fr);
    }

    @media (min-width: 800px) {
        // grid-template-columns: repeat(3, 1fr);
    }

    @media (min-width: 1300px) {
        // grid-template-columns: repeat(4, 300px);
    }

    @media (min-width: 1700px) {
        // grid-template-columns: repeat(5, 300px);
    }

    &__buttons {
        column-gap: 10px;
    }

    &__button {
        &--green {
            background: #0cd35d;
        }
    }
}
</style>
